import { useEffect } from 'react'
import { Alert, Button, Grid, Link, Stack, Typography } from '@mui/material'
import { RHFTextField, RHFSearchableSelectField } from '../../../common/hook-form'
import { useFormContext } from 'react-hook-form'
import { useGenericPostRequestQuery } from '../../../redux/commonRTK'
import { apiConstants } from '../../../common/constants'
import { getMerchant } from '../../../helpers/ReduxHelpers'

function PackageDetails({ setActiveStep, creatingOrder }) {
    const { trigger, setValue, watch } = useFormContext()

    const merchant = getMerchant()
    const { data: packageList } = useGenericPostRequestQuery({
        url: apiConstants.GET_PACKAGES,
        method: 'GET',
        data: { merchantOid: merchant.id },
    })
    const hasPackages = packageList?.length > 0

    const selectedPackageId = watch('packageId')
    const l = watch('shipment_detail.0.item_length')
    const b = watch('shipment_detail.0.item_breadth')
    const h = watch('shipment_detail.0.item_height')
    const volWeight = l && b && h
        ? ((Number(l) * Number(b) * Number(h)) / 5000).toFixed(2)
        : null

    useEffect(() => {
        if (!selectedPackageId || !packageList) return
        const pkg = packageList.find(p => p.id === selectedPackageId)
        if (!pkg) return
        setValue('shipment_detail.0.item_length', pkg.length_cm)
        setValue('shipment_detail.0.item_breadth', pkg.breadth_cm)
        setValue('shipment_detail.0.item_height', pkg.height_cm)
    }, [selectedPackageId, packageList, setValue])

    const onNextHandler = async () => {
        const status = await trigger([
            'shipment_detail.0.item_weight',
            'shipment_detail.0.item_length',
            'shipment_detail.0.item_breadth',
            'shipment_detail.0.item_height',
        ])
        if (status) setActiveStep(4)
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="caption" fontWeight={500}>
                    <strong>Note:</strong> All package dimensions must be specified in centimeters (cm) &amp; weight in grams
                </Typography>
            </Grid>

            {!hasPackages && (
                <Grid item xs={12}>
                    <Alert severity="info" sx={{ fontSize: 12 }}>
                        No master packages configured. Add packages in{' '}
                        <Link href="/setting/package" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>
                            Master Packaging
                        </Link>{' '}
                        for faster order creation.
                    </Alert>
                </Grid>
            )}

            {hasPackages && (
                <Grid item xs={12} md={6}>
                    <RHFSearchableSelectField
                        variant="filled"
                        width="100%"
                        name="packageId"
                        label="Select Package (LxBxH) - (cm)"
                        options={packageList.map(pkg => ({
                            label: `${pkg.box_name} - (${pkg.length_cm} x ${pkg.breadth_cm} x ${pkg.height_cm})`,
                            value: pkg.id,
                        }))}
                    />
                </Grid>
            )}

            <Grid item xs={12} md={3}>
                <RHFTextField variant="filled" name="shipment_detail.0.item_weight" label="Weight (gms) *" type="number" placeholder="Enter Weight" />
            </Grid>
            <Grid item xs={12} md={3}>
                <RHFTextField variant="filled" name="shipment_detail.0.item_length" label="Length (cm) *" type="number" placeholder="Enter Length" />
            </Grid>
            <Grid item xs={12} md={3}>
                <RHFTextField variant="filled" name="shipment_detail.0.item_breadth" label="Breadth (cm) *" type="number" placeholder="Enter Breadth" />
            </Grid>
            <Grid item xs={12} md={3}>
                <RHFTextField variant="filled" name="shipment_detail.0.item_height" label="Height (cm) *" type="number" placeholder="Enter Height" />
            </Grid>

            {volWeight && (
                <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Volumetric Weight: <strong>{volWeight} kg</strong>
                    </Typography>
                </Grid>
            )}

            <Grid item xs={12}>
                <Stack direction="row" columnGap={1} sx={{ mt: 3 }}>
                    <Button variant="contained" size="small" color="error" onClick={() => setActiveStep(2)}>Back</Button>
                    <Button variant="contained" size="small" onClick={onNextHandler}>Next</Button>
                </Stack>
            </Grid>
        </Grid>
    )
}

export default PackageDetails
